import { useState, useEffect } from 'react';
import { X, FileText, Plus, Trash2, Save } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

/**
 * Modal for managing business rules for entities and fields
 * Supports Markdown formatting
 * Integrated with Data Dictionary, Objects Library, and SQL Export
 */
const BusinessRulesModal = ({ isOpen, onClose, entity, onSave }) => {
  const [activeTab, setActiveTab] = useState('entity'); // 'entity' or 'fields'
  const [entityRule, setEntityRule] = useState('');
  const [fieldRules, setFieldRules] = useState({});
  const [selectedField, setSelectedField] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (isOpen && entity) {
      // Load existing rules
      setEntityRule(entity.data?.businessRules?.entity || '');
      setFieldRules(entity.data?.businessRules?.fields || {});
      setSelectedField(null);
      setActiveTab('entity');
      setPreviewMode(false);
    }
  }, [isOpen, entity]);

  if (!isOpen || !entity) return null;

  const fields = entity.data?.fields || [];
  const entityName = entity.data?.name || entity.data?.label || entity.id;

  const handleSave = () => {
    const businessRules = {
      entity: entityRule.trim(),
      fields: Object.keys(fieldRules).reduce((acc, fieldName) => {
        if (fieldRules[fieldName]?.trim()) {
          acc[fieldName] = fieldRules[fieldName].trim();
        }
        return acc;
      }, {})
    };

    onSave(businessRules);
    onClose();
  };

  const handleFieldRuleChange = (fieldName, value) => {
    setFieldRules(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleClearFieldRule = (fieldName) => {
    setFieldRules(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

  const getFieldsWithRules = () => {
    return Object.keys(fieldRules).filter(fieldName => fieldRules[fieldName]?.trim());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="text-blue-600 dark:text-blue-400" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Business Rules
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {entityName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-4">
          <button
            onClick={() => setActiveTab('entity')}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'entity'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Entity Rules
            {entityRule.trim() && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                ✓
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('fields')}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'fields'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Field Rules
            {getFieldsWithRules().length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                {getFieldsWithRules().length}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Entity Rules Tab */}
          {activeTab === 'entity' && (
            <div className="flex-1 p-4 overflow-auto">
              {/* Markdown Help */}
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-300 mb-2 font-medium">
                  Markdown Support
                </p>
                <div className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                  <p>**bold** | *italic* | `code` | [link](url)</p>
                  <p># Heading | - List | &gt; Quote</p>
                </div>
              </div>

              {/* Preview Toggle */}
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {previewMode ? '✏️ Edit' : '👁️ Preview'}
                </button>
              </div>

              {/* Editor or Preview */}
              {!previewMode ? (
                <textarea
                  value={entityRule}
                  onChange={(e) => setEntityRule(e.target.value)}
                  placeholder={`Business rules for ${entityName}...\n\nExample:\n- This table stores customer information\n- Email must be unique across all customers\n- Status can be: active, inactive, suspended\n- Created_at is automatically set on insert`}
                  className="w-full h-96 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <div className="w-full h-96 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 overflow-auto prose prose-sm dark:prose-invert max-w-none">
                  {entityRule.trim() ? (
                    <ReactMarkdown>{entityRule}</ReactMarkdown>
                  ) : (
                    <p className="text-gray-400 italic">No rules defined</p>
                  )}
                </div>
              )}

              {/* Character Count */}
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-right">
                {entityRule.length} characters
              </div>
            </div>
          )}

          {/* Field Rules Tab */}
          {activeTab === 'fields' && (
            <div className="flex-1 flex overflow-hidden">
              {/* Field List */}
              <div className="w-64 border-r border-gray-200 dark:border-gray-700 overflow-auto">
                <div className="p-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase px-2 py-1">
                    Fields
                  </p>
                  {fields.length === 0 ? (
                    <p className="text-sm text-gray-400 italic p-2">No fields</p>
                  ) : (
                    <div className="space-y-1">
                      {fields.map((field) => (
                        <button
                          key={field.name}
                          onClick={() => setSelectedField(field.name)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedField === field.name
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono">{field.name}</span>
                            {fieldRules[field.name]?.trim() && (
                              <span className="text-xs text-green-600 dark:text-green-400">✓</span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {field.type}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Field Rule Editor */}
              <div className="flex-1 p-4 overflow-auto">
                {selectedField ? (
                  <>
                    {/* Field Header */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white font-mono">
                          {selectedField}
                        </h3>
                        {fieldRules[selectedField]?.trim() && (
                          <button
                            onClick={() => handleClearFieldRule(selectedField)}
                            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-1"
                          >
                            <Trash2 size={14} />
                            Clear
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {fields.find(f => f.name === selectedField)?.type}
                      </p>
                    </div>

                    {/* Preview Toggle */}
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={() => setPreviewMode(!previewMode)}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        {previewMode ? '✏️ Edit' : '👁️ Preview'}
                      </button>
                    </div>

                    {/* Editor or Preview */}
                    {!previewMode ? (
                      <textarea
                        value={fieldRules[selectedField] || ''}
                        onChange={(e) => handleFieldRuleChange(selectedField, e.target.value)}
                        placeholder={`Business rules for ${selectedField}...\n\nExample:\n- Must be a valid email format\n- Cannot be changed after creation\n- Used for login authentication\n- Automatically converted to lowercase`}
                        className="w-full h-64 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 overflow-auto prose prose-sm dark:prose-invert max-w-none">
                        {fieldRules[selectedField]?.trim() ? (
                          <ReactMarkdown>{fieldRules[selectedField]}</ReactMarkdown>
                        ) : (
                          <p className="text-gray-400 italic">No rules defined for this field</p>
                        )}
                      </div>
                    )}

                    {/* Character Count */}
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-right">
                      {(fieldRules[selectedField] || '').length} characters
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 italic">
                    Select a field to define business rules
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {entityRule.trim() && (
              <span className="mr-4">✓ Entity rule defined</span>
            )}
            {getFieldsWithRules().length > 0 && (
              <span>{getFieldsWithRules().length} field rule(s) defined</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save size={18} />
              Save Rules
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessRulesModal;
